// Simple History API router for the application
import type { AppContext } from "./types/index.js";
import { displayDeviceInfo } from "./main.js";
import { logger } from "./utils/logger.ts";
import { createStage } from "./components/Stage/Stage.ts";
import { createIconTest } from "./components/UI/IconTest.js";
import { createAppStateManager } from "./data/app-state-manager.js";
import {
  createMobileConsole,
  enableTripleTapConsole,
} from "./utils/mobile-console.js";

export interface RouteHandler {
  readonly render: (
    container: HTMLElement,
    appContext: AppContext,
    params?: Record<string, string>
  ) => { destroy: () => void };
}

export interface RouterAPI {
  readonly navigate: (path: string) => void;
  readonly getCurrentRoute: () => string;
  readonly destroy: () => void;
}

/**
 * Creates a simple History API router
 * @param container - Root container for route content
 * @param appContext - Global app context
 * @returns Router API
 */
export const createRouter = (
  container: HTMLElement,
  appContext: AppContext
): RouterAPI => {
  let currentRoute: string = "/";
  let currentComponent: { destroy: () => void } | null = null;
  let isDestroyed = false;
  let unsubscribeRouteUpdate: (() => void) | null = null;

  // Route handlers
  const routes: Record<string, RouteHandler> = {
    "/": {
      render: (container, appContext) => renderHome(container, appContext),
    },
    "/stage": {
      render: (container, appContext) =>
        renderStage(container, appContext, { mode: "edit" }),
    },
    "/sketch": {
      render: (container, appContext, params) =>
        renderStage(container, appContext, {
          mode: "sketch",
          projectId: params?.projectId,
          sketchId: params?.sketchId,
          forceMode: true,
        }),
    },
    "/auth": {
      render: (container, appContext) => renderAuthDemo(container, appContext),
    },
  };

  /**
   * Render home route (existing functionality)
   */
  const renderHome = (container: HTMLElement, appContext: AppContext) => {
    container.innerHTML = `
      <div class="app-container">
        <header class="app-header">
          <h1>Sketch Component - Test Environment</h1>
          <div class="device-info">
            <span id="device-info"></span>
          </div>
          <nav class="nav">
            <a href="/stage" class="nav-link">Go to Stage</a>
            <a href="/auth" class="nav-link">Login</a>
          </nav>
        </header>
        
        <main class="app-main">
          <div class="sketch-container" id="sketch-container">
            <div class="placeholder">
              <p>Sketch Component Loading...</p>
            </div>
          </div>
          
          <div class="test-controls" id="test-controls">
            <h3>Test Controls</h3>
            <button id="clear-canvas">Clear</button>
            <button id="toggle-tool">Toggle Tool</button>
            <button id="export-sketch">Export</button>
          </div>
        </main>
      </div>
    `;

    // Setup test controls and device info
    setupTestControls(appContext);
    displayDeviceInfo((appContext as any).deviceInfo);

    return {
      destroy: () => {
        // Clean up home route if needed
      },
    };
  };

  /**
   * Render stage route with Stage component
   */
  const renderStage = (
    container: HTMLElement,
    appContext: AppContext,
    options?: {
      mode?: "edit" | "sketch";
      projectId?: string;
      sketchId?: string;
      forceMode?: boolean;
    }
  ) => {
    container.innerHTML = `
      <div class="stage-container">
        <header class="stage-header">
          <h1>Stage Environment</h1>
          <nav class="nav">
            <a href="/" class="nav-link">← Back to Home</a>
          </nav>
        </header>
        <div class="stage-workspace" id="stage-workspace"></div>
      </div>
    `;

    // Add mobile console for iPad testing
    const mobileConsole = createMobileConsole();

    // Get the stage workspace container
    const stageWorkspace = container.querySelector(
      "#stage-workspace"
    ) as HTMLElement;

    // Create app state manager for Stage
    const appStateManagerConfig = {
      onUIStateChange: (state: any) => {
        logger.debug("ui", "🎯 UI State changed:", state);
        // Emit UI state change through app event bus so components can react
        appContext.eventBus.emit("ui-state-changed", { state });
      },
      onUserPreferencesChange: (prefs: any) => {
        logger.debug("ui", "👤 User preferences changed:", prefs);
      },
    };
    const appStateManager = createAppStateManager(
      appContext,
      appStateManagerConfig
    );

    // Add icon test (temporary for development)
    const iconTest = createIconTest(container);

    // Create and mount the Stage component with app state
    const stage = createStage(stageWorkspace, appContext, {
      mode: "edit",
      // initialTransform removed - let Stage restore from saved state
      onToggleIconModal: iconTest.toggle,
      appStateManager, // Pass app state to Stage
    });

    if (options?.mode && appStateManager.uiState?.setMode) {
      const existingMode =
        appStateManager.uiState.getState?.()?.selectedMode ?? null;
      if (!existingMode || options.forceMode) {
        appStateManager.uiState.setMode(options.mode as any);
      }
    }

    if (options?.projectId) {
      const projectId = options.projectId;
      const sketchId = options.sketchId;
      let unsubscribeProjectMatch: (() => void) | null = null;
      const handleProjectMatch = (payload: any) => {
        if (payload?.projectId !== projectId) return;
        unsubscribeProjectMatch?.();
        if (sketchId) {
          appContext.eventBus.emit("sketch-selected", { sketchId });
        }
      };
      unsubscribeProjectMatch =
        appContext.eventBus.on?.("project-changed", handleProjectMatch) ?? null;
      appContext.eventBus.emit("project-selected", { projectId });
      if (!sketchId) {
        unsubscribeProjectMatch?.();
      }
    } else if (options?.sketchId) {
      appContext.eventBus.emit("sketch-selected", {
        sketchId: options.sketchId,
      });
    }

    console.log("🎭 Stage component initialized successfully");

    return {
      destroy: () => {
        stage.destroy();
        iconTest.destroy();
        appStateManager.destroy();
        console.log("🧹 Stage route destroyed");
      },
    };
  };

  /**
   * Render auth demo route using backend API helpers
   */
  const renderAuthDemo = (container: HTMLElement, _appContext: AppContext) => {
    // Initial lightweight shell while we check auth
    container.innerHTML = `
      <div class="auth-demo">
        <header class="stage-header">
          <h1>Login</h1>
          <nav class="nav">
            <a href="/" class="nav-link">← Back to Home</a>
          </nav>
        </header>
        <section class="auth-actions" style="display:grid; gap:12px; max-width:520px;">
          <div id="auth-status">Checking authentication…</div>
        </section>
      </div>
    `;

    // Lazy import to avoid coupling at top-level
    import("./api.js")
      .then(
        async ({
          startGoogleLogin,
          requestMagicLink,
          getMe,
          createProject,
          completeMagicToken,
          logout,
        }: any) => {
          // Pre-check: if already authenticated, go straight to stage
          try {
            await getMe();
            window.history.pushState({}, "", "/stage");
            window.dispatchEvent(new PopStateEvent("popstate"));
            return { destroy: () => {} };
          } catch {
            // Not authenticated; render full UI below
          }

          container.innerHTML = `
          <div class="auth-demo">
            <header class="stage-header">
              <h1>Login</h1>
              <nav class="nav">
                <a href="/" class="nav-link">← Back to Home</a>
              </nav>
            </header>
            <section class="auth-actions" style="display:grid; gap:12px; max-width:520px;">
              <button id="google-login">Sign in with Google</button>
              <form id="magic-form" style="display:flex; gap:8px;">
                <input id="magic-email" type="email" placeholder="email@domain.com" required style="flex:1;" />
                <button type="submit">Send Magic Link</button>
              </form>
              <div style="display:flex; gap:8px;">
                <button id="dev-login" type="button">Dev Login (email above)</button>
              </div>
              <div style="display:flex; gap:8px;">
                <input id="magic-token" type="text" placeholder="Paste token (dev)" style="flex:1;" />
                <button id="magic-complete" type="button">Complete Magic</button>
              </div>
              <div style="display:flex; gap:8px; flex-wrap:wrap;">
                <button id="check-me">Who am I?</button>
                <button id="load-session" type="button">Load Session</button>
                <button id="logout" type="button">Logout</button>
              </div>
              <pre id="me-output" style="background:#111; color:#0f0; padding:8px; min-height:40px;"></pre>
              <hr />
              <form id="create-project" style="display:flex; gap:8px;">
                <input id="proj-name" type="text" placeholder="Project name" required style="flex:1;" />
                <button type="submit">Create Project</button>
              </form>
              <pre id="proj-output" style="background:#111; color:#0f0; padding:8px; min-height:40px;"></pre>
            </section>
          </div>
        `;

          const googleBtn = container.querySelector(
            "#google-login"
          ) as HTMLButtonElement | null;
          const magicForm = container.querySelector(
            "#magic-form"
          ) as HTMLFormElement | null;
          const magicEmail = container.querySelector(
            "#magic-email"
          ) as HTMLInputElement | null;
          const checkMeBtn = container.querySelector(
            "#check-me"
          ) as HTMLButtonElement | null;
          const magicToken = container.querySelector(
            "#magic-token"
          ) as HTMLInputElement | null;
          const magicComplete = container.querySelector(
            "#magic-complete"
          ) as HTMLButtonElement | null;
          const devLoginBtn = container.querySelector(
            "#dev-login"
          ) as HTMLButtonElement | null;
          const meOut = container.querySelector(
            "#me-output"
          ) as HTMLElement | null;
          const loadSessionBtn = container.querySelector(
            "#load-session"
          ) as HTMLButtonElement | null;
          const logoutBtn = container.querySelector(
            "#logout"
          ) as HTMLButtonElement | null;
          const createForm = container.querySelector(
            "#create-project"
          ) as HTMLFormElement | null;
          const projName = container.querySelector(
            "#proj-name"
          ) as HTMLInputElement | null;
          const projOut = container.querySelector(
            "#proj-output"
          ) as HTMLElement | null;

          googleBtn?.addEventListener("click", () => startGoogleLogin());

          magicForm?.addEventListener("submit", async (e) => {
            e.preventDefault();
            const email = magicEmail?.value?.trim();
            if (!email) return;
            try {
              const resp = await requestMagicLink(email);
              if (resp?.debug?.token && magicToken) {
                magicToken.value = resp.debug.token;
              }
              alert(
                "Magic link requested. Check server logs/email. In dev, token is prefilled."
              );
            } catch (err) {
              alert("Failed to request magic link");
              console.error(err);
            }
          });

          magicComplete?.addEventListener("click", async () => {
            const token = magicToken?.value?.trim();
            if (!token) return alert("Paste token first");
            meOut!.textContent = "Completing...";
            try {
              const json = await completeMagicToken(token);
              meOut!.textContent = JSON.stringify(json, null, 2);
            } catch (err) {
              meOut!.textContent = "Failed to complete magic link";
              console.error(err);
            }
          });

          devLoginBtn?.addEventListener("click", async () => {
            const email = magicEmail?.value?.trim();
            if (!email) return alert("Enter email above");
            meOut!.textContent = "Dev login...";
            try {
              const res = await fetch("/api/auth/dev-login", {
                method: "POST",
                headers: { "content-type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email }),
              });
              const json = await res.json();
              // Backend now returns the session payload directly
              meOut!.textContent = JSON.stringify(json, null, 2);
              // Save into session manager and go to stage
              try {
                (window as any).app
                  ?.getContext?.()
                  .eventBus.emit("session:updated", { session: json });
              } catch {}
              window.history.pushState({}, "", "/stage");
              window.dispatchEvent(new PopStateEvent("popstate"));
            } catch (e) {
              meOut!.textContent = "Dev login failed";
            }
          });

          checkMeBtn?.addEventListener("click", async () => {
            meOut!.textContent = "Loading...";
            try {
              const me = await getMe();
              meOut!.textContent = JSON.stringify(me, null, 2);
            } catch (err: any) {
              meOut!.textContent = `Error: ${err?.response?.status || ""}`;
            }
          });

          loadSessionBtn?.addEventListener("click", async () => {
            meOut!.textContent = "Loading session...";
            try {
              const res = await fetch("/api/auth/session", {
                credentials: "include",
              });
              const json = await res.json();
              meOut!.textContent = JSON.stringify(json, null, 2);
              try {
                (window as any).app
                  ?.getContext?.()
                  .eventBus.emit("session:updated", { session: json });
              } catch {}
            } catch (err) {
              meOut!.textContent = "Failed to load session";
            }
          });

          logoutBtn?.addEventListener("click", async () => {
            meOut!.textContent = "Logging out...";
            try {
              const res = await logout();
              meOut!.textContent = JSON.stringify(res, null, 2);
            } catch (err) {
              meOut!.textContent = "Logout failed";
            }
          });

          createForm?.addEventListener("submit", async (e) => {
            e.preventDefault();
            const name = projName?.value?.trim();
            if (!name) return;
            projOut!.textContent = "Creating...";
            try {
              const res = await createProject({ name });
              projOut!.textContent = JSON.stringify(res, null, 2);
            } catch (err: any) {
              projOut!.textContent = `Error: ${err?.response?.status || ""}`;
            }
          });

          return { destroy: () => {} };
        }
      )
      .catch((e) => console.error("Failed to load api helpers", e));

    return { destroy: () => {} };
  };

  /**
   * Setup test controls (moved from main.ts)
   */
  const setupTestControls = (appContext: AppContext): void => {
    const clearButton = document.getElementById("clear-canvas");
    const toggleButton = document.getElementById("toggle-tool");
    const exportButton = document.getElementById("export-sketch");

    clearButton?.addEventListener("click", () => {
      appContext.eventBus.emit("canvas-clear", {});
      console.log("🧹 Clear canvas requested");
    });

    toggleButton?.addEventListener("click", () => {
      appContext.eventBus.emit("tool-toggle", {});
      console.log("🔧 Tool toggle requested");
    });

    exportButton?.addEventListener("click", () => {
      appContext.eventBus.emit("canvas-export", {});
      console.log("📤 Export requested");
    });
  };

  /**
   * Navigate to a route
   */
  const navigate = (path: string): void => {
    if (isDestroyed) return;

    // Clean up current component
    if (currentComponent) {
      currentComponent.destroy();
      currentComponent = null;
    }

    // Update browser history
    window.history.pushState({}, "", path);
    currentRoute = path;

    // Render new route
    const match = resolveRoute(path);
    if (match?.handler) {
      currentComponent = match.handler.render(
        container,
        appContext,
        match.params
      );
    } else {
      console.warn(`🚨 Route not found: ${path}`);
      navigate("/"); // Fallback to home
    }
  };

  /**
   * Handle browser back/forward
   */
  const handlePopState = (): void => {
    if (isDestroyed) return;

    const path = window.location.pathname || "/";
    if (path !== currentRoute) {
      // Don't use navigate() to avoid pushing duplicate state
      currentRoute = path;

      // Clean up current component
      if (currentComponent) {
        currentComponent.destroy();
        currentComponent = null;
      }

      // Render new route
      const match = resolveRoute(path);
      if (match?.handler) {
        currentComponent = match.handler.render(
          container,
          appContext,
          match.params
        );
      } else {
        console.warn(`🚨 Route not found: ${path}`);
        currentRoute = "/";
        window.history.replaceState({}, "", "/");
        const homeHandler = resolveRoute("/");
        if (homeHandler?.handler) {
          currentComponent = homeHandler.handler.render(
            container,
            appContext,
            homeHandler.params
          );
        }
      }
    }
  };

  const resolveRoute = (
    path: string
  ):
    | { handler: RouteHandler; params?: Record<string, string> }
    | null => {
    if (routes[path]) {
      return { handler: routes[path] };
    }
    const sketchMatch = path.match(/^\/sketch\/([^/]+)\/([^/]+)$/);
    if (sketchMatch) {
      return {
        handler: routes["/sketch"],
        params: { projectId: sketchMatch[1]!, sketchId: sketchMatch[2]! },
      };
    }
    return null;
  };

  /**
   * Initialize router
   */
  const init = (): void => {
    // Listen for browser back/forward
    window.addEventListener("popstate", handlePopState);

    if (appContext.eventBus?.on) {
      unsubscribeRouteUpdate = appContext.eventBus.on(
        "route:update",
        (payload: { path?: string; replace?: boolean } | undefined) => {
          const path = payload?.path;
          if (!path) return;
          if (payload?.replace) {
            window.history.replaceState({}, "", path);
          } else {
            window.history.pushState({}, "", path);
          }
          currentRoute = path;
        }
      );
    }

    // Handle link clicks to prevent full page reload
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");

      if (link && link.href) {
        const url = new URL(link.href);

        // Only handle same-origin links
        if (url.origin === window.location.origin) {
          e.preventDefault();
          navigate(url.pathname);
        }
      }
    });

    // Handle initial route
    const initialPath = window.location.pathname || "/";
    navigate(initialPath);
  };

  // Initialize immediately
  init();

  return {
    navigate,
    getCurrentRoute: () => currentRoute,
    destroy: () => {
      isDestroyed = true;
      window.removeEventListener("popstate", handlePopState);
      unsubscribeRouteUpdate?.();
      unsubscribeRouteUpdate = null;

      if (currentComponent) {
        currentComponent.destroy();
        currentComponent = null;
      }

      console.log("🧹 Router destroyed");
    },
  };
};
